import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, ArrowRight, Repeat, Calendar, Syringe, Check, Edit2, X, Play, Pause, AlertTriangle } from 'lucide-react';
import { useResults } from '../lib/useResults';
import { SaveResultButton } from '../components/tools/SaveResultButton';

// User-entered peptide cycle data (dose, frequency, start dates).
// localStorage is intentional: this is a client-side calculator/tracker —
// no auth, no PII, no payment data. XSS-safe in this context.
const STORAGE_KEY = 'ninja_peptide_cycles';
const getStoredCycles = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; } };
const persistCycles = (c) => localStorage.setItem(STORAGE_KEY, JSON.stringify(c));

const PEPTIDE_TEMPLATES = [
  { name: 'BPC-157', dose: '250 mcg', frequency: '2x/day', route: 'SubQ', cycleOn: 28, cycleOff: 14, color: '#E63946' },
  { name: 'TB-500', dose: '2.5 mg', frequency: '2x/week (loading) then 1x/week', route: 'SubQ', cycleOn: 56, cycleOff: 28, color: '#E63946' },
  { name: 'CJC-1295/Ipamorelin', dose: '300 mcg', frequency: '1x/day (before bed)', route: 'SubQ', cycleOn: 84, cycleOff: 28, color: '#00F0FF' },
  { name: 'Semaglutide', dose: '0.25-2.4 mg', frequency: '1x/week', route: 'SubQ', cycleOn: 112, cycleOff: 0, color: '#22c55e' },
  { name: 'GHK-Cu', dose: '200 mcg', frequency: '1x/day', route: 'SubQ', cycleOn: 28, cycleOff: 14, color: '#a78bfa' },
  { name: 'PT-141', dose: '2 mg', frequency: 'As needed (max 2x/week)', route: 'SubQ', cycleOn: 0, cycleOff: 0, color: '#f59e0b' },
  { name: 'MOTS-C', dose: '5 mg', frequency: '3x/week', route: 'SubQ', cycleOn: 28, cycleOff: 14, color: '#00B4D8' },
  { name: 'Epitalon', dose: '5 mg', frequency: '1x/day', route: 'SubQ', cycleOn: 10, cycleOff: 180, color: '#818cf8' },
];

const daysBetween = (d1, d2) => Math.round((new Date(d2) - new Date(d1)) / (1000 * 60 * 60 * 24));
const addDays = (date, days) => { const d = new Date(date); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

const STATUS_COLORS = { active: '#22c55e', paused: '#f59e0b', completed: '#00F0FF', upcoming: '#a78bfa' };

const getCycleStatus = (cycle) => {
  const now = today();
  if (cycle.paused) return 'paused';
  if (cycle.startDate > now) return 'upcoming';
  const endDate = cycle.endDate || addDays(cycle.startDate, cycle.cycleOnDays);
  if (now > endDate) return 'completed';
  return 'active';
};

const CycleTimeline = ({ cycles }) => {
  if (cycles.length === 0) return null;
  const allDates = cycles.flatMap(c => {
    const end = c.endDate || addDays(c.startDate, c.cycleOnDays);
    return [c.startDate, end];
  });
  const minDate = allDates.sort()[0];
  const maxDate = allDates.sort().reverse()[0];
  const totalDays = Math.max(daysBetween(minDate, maxDate), 7);

  return (
    <div className="bg-zinc-900/80 border border-zinc-800 p-5 mb-6" data-testid="cycle-timeline">
      <h3 className="text-white font-heading text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-[#E63946]" /> Cycle Timeline
      </h3>
      <div className="space-y-3">
        {cycles.map(c => {
          const end = c.endDate || addDays(c.startDate, c.cycleOnDays);
          const startOffset = (daysBetween(minDate, c.startDate) / totalDays) * 100;
          const duration = (daysBetween(c.startDate, end) / totalDays) * 100;
          const status = getCycleStatus(c);
          return (
            <div key={c.id} className="relative">
              <div className="flex items-center gap-3 mb-1">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color || '#E63946' }} />
                <span className="text-white text-xs font-heading">{c.peptideName}</span>
                <span className="text-zinc-600 text-[10px]">{c.dose} — {c.frequency}</span>
                <span className="ml-auto px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-heading border" style={{ color: STATUS_COLORS[status], borderColor: `${STATUS_COLORS[status]}40` }}>{status}</span>
              </div>
              <div className="h-3 bg-zinc-800 relative overflow-hidden">
                <div className="absolute h-full transition-all duration-300" style={{ left: `${Math.max(startOffset, 0)}%`, width: `${Math.min(duration, 100 - startOffset)}%`, backgroundColor: `${c.color || '#E63946'}40`, borderLeft: `2px solid ${c.color || '#E63946'}` }} />
                {/* Today marker */}
                {(() => {
                  const todayOffset = (daysBetween(minDate, today()) / totalDays) * 100;
                  if (todayOffset >= 0 && todayOffset <= 100) return <div className="absolute h-full w-px bg-white/40" style={{ left: `${todayOffset}%` }} />;
                  return null;
                })()}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-2 text-zinc-600 text-[10px]">
        <span>{minDate}</span>
        <span className="text-white/40">Today</span>
        <span>{maxDate}</span>
      </div>
    </div>
  );
};

export default function PeptideCycleTrackerPage() {
  const [cycles, setCycles] = useState(getStoredCycles);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ peptideName: '', dose: '', frequency: '', route: 'SubQ', startDate: today(), cycleOnDays: 28, cycleOffDays: 14, notes: '', color: '#E63946' });
  const [saved, setSaved] = useState(false);
  const { saveResult } = useResults();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyTemplate = (tmpl) => {
    setForm(f => ({ ...f, peptideName: tmpl.name, dose: tmpl.dose, frequency: tmpl.frequency, route: tmpl.route, cycleOnDays: tmpl.cycleOn, cycleOffDays: tmpl.cycleOff, color: tmpl.color }));
  };

  const handleSubmit = useCallback(() => {
    if (!form.peptideName || !form.dose || !form.startDate) return;
    const cycle = {
      id: editId || Date.now(),
      ...form,
      cycleOnDays: parseInt(form.cycleOnDays) || 28,
      cycleOffDays: parseInt(form.cycleOffDays) || 0,
      endDate: addDays(form.startDate, parseInt(form.cycleOnDays) || 28),
      paused: false,
      createdAt: new Date().toISOString(),
    };
    let updated;
    if (editId) {
      updated = cycles.map(c => c.id === editId ? { ...c, ...cycle } : c);
    } else {
      updated = [cycle, ...cycles];
    }
    setCycles(updated);
    persistCycles(updated);
    setForm({ peptideName: '', dose: '', frequency: '', route: 'SubQ', startDate: today(), cycleOnDays: 28, cycleOffDays: 14, notes: '', color: '#E63946' });
    setShowForm(false);
    setEditId(null);
  }, [form, cycles, editId]);

  const deleteCycle = (id) => {
    const updated = cycles.filter(c => c.id !== id);
    setCycles(updated);
    persistCycles(updated);
  };

  const togglePause = (id) => {
    const updated = cycles.map(c => c.id === id ? { ...c, paused: !c.paused } : c);
    setCycles(updated);
    persistCycles(updated);
  };

  const editCycle = (cycle) => {
    setForm({ peptideName: cycle.peptideName, dose: cycle.dose, frequency: cycle.frequency, route: cycle.route, startDate: cycle.startDate, cycleOnDays: cycle.cycleOnDays, cycleOffDays: cycle.cycleOffDays, notes: cycle.notes || '', color: cycle.color || '#E63946' });
    setEditId(cycle.id);
    setShowForm(true);
  };

  const activeCycles = cycles.filter(c => getCycleStatus(c) === 'active');
  const otherCycles = cycles.filter(c => getCycleStatus(c) !== 'active');

  const handleSave = () => {
    saveResult('peptideCycle', {
      value: `${activeCycles.length} active`,
      label: activeCycles.map(c => c.peptideName).join(', ') || 'No active cycles',
      totalCycles: cycles.length,
    });
    setSaved(true);
  };

  return (
    <main className="pt-20 min-h-screen" data-testid="peptide-cycle-page">
      <section className="container-ninja section-spacing">
        <div className="max-w-3xl mx-auto">
          <Link to="/tools" className="text-zinc-500 text-xs font-heading uppercase tracking-wider hover:text-white transition-colors inline-flex items-center gap-1 mb-6" data-testid="back-to-tools">
            <ArrowLeft className="w-3 h-3" /> All Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 flex items-center justify-center bg-[#E63946]/10 border border-[#E63946]/30">
              <Repeat className="w-5 h-5 text-[#E63946]" />
            </div>
            <h1 className="text-3xl sm:text-4xl text-white font-heading" data-testid="page-title">Peptide Cycle Tracker</h1>
          </div>
          <p className="text-zinc-400 text-sm mb-8">Track your peptide cycles with start/end dates, dosing schedules, and on/off cycling. All data stored locally.</p>

          {/* Active Cycles Summary */}
          {activeCycles.length > 0 && (
            <div className="bg-zinc-900/80 border border-green-500/20 p-4 mb-6 flex items-center gap-4" data-testid="active-summary">
              <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
              <div>
                <p className="text-white text-sm font-heading">{activeCycles.length} Active Cycle{activeCycles.length > 1 ? 's' : ''}</p>
                <p className="text-zinc-400 text-xs">{activeCycles.map(c => c.peptideName).join(', ')}</p>
              </div>
            </div>
          )}

          {/* Timeline */}
          {cycles.length > 0 && <CycleTimeline cycles={cycles} />}

          {/* Add Cycle Button */}
          {!showForm && (
            <button onClick={() => { setShowForm(true); setEditId(null); }}
              className="w-full py-3 bg-zinc-900/80 border border-dashed border-zinc-700 hover:border-[#E63946]/40 text-zinc-400 hover:text-[#E63946] transition-all flex items-center justify-center gap-2 font-heading text-sm uppercase tracking-wider mb-6"
              data-testid="add-cycle-btn">
              <Plus className="w-4 h-4" /> Add New Cycle
            </button>
          )}

          {/* Add/Edit Form */}
          {showForm && (
            <div className="bg-zinc-900/80 border border-[#E63946]/20 p-6 mb-6" data-testid="cycle-form">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-heading text-lg flex items-center gap-2">
                  <Syringe className="w-4 h-4 text-[#E63946]" /> {editId ? 'Edit Cycle' : 'New Cycle'}
                </h3>
                <button onClick={() => { setShowForm(false); setEditId(null); }} className="text-zinc-500 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Template Quick Select */}
              <div className="mb-4">
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-2 font-heading">Quick Select</label>
                <div className="flex flex-wrap gap-2">
                  {PEPTIDE_TEMPLATES.map(t => (
                    <button key={t.name} onClick={() => applyTemplate(t)}
                      className={`px-2.5 py-1 text-[10px] font-heading uppercase tracking-wider border transition-colors ${
                        form.peptideName === t.name ? 'border-[#E63946]/40 bg-[#E63946]/5' : 'text-zinc-500 border-zinc-700 hover:text-white'
                      }`}
                      style={form.peptideName === t.name ? { color: t.color } : {}}
                      data-testid={`template-${t.name.toLowerCase().replace(/[\s\/]+/g, '-')}`}>
                      {t.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Peptide Name *</label>
                  <input type="text" value={form.peptideName} onChange={e => set('peptideName', e.target.value)} placeholder="e.g. BPC-157"
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-peptide-name" />
                </div>
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Dose *</label>
                  <input type="text" value={form.dose} onChange={e => set('dose', e.target.value)} placeholder="e.g. 250 mcg"
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-dose" />
                </div>
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Frequency</label>
                  <input type="text" value={form.frequency} onChange={e => set('frequency', e.target.value)} placeholder="e.g. 2x/day"
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-frequency" />
                </div>
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Route</label>
                  <select value={form.route} onChange={e => set('route', e.target.value)}
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-route">
                    <option value="SubQ">Subcutaneous (SubQ)</option>
                    <option value="IM">Intramuscular (IM)</option>
                    <option value="Oral">Oral</option>
                    <option value="Nasal">Nasal</option>
                    <option value="Topical">Topical</option>
                  </select>
                </div>
                <div>
                  <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Start Date *</label>
                  <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)}
                    className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-start-date" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">On (days)</label>
                    <input type="number" value={form.cycleOnDays} onChange={e => set('cycleOnDays', e.target.value)} min="1"
                      className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-cycle-on" />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Off (days)</label>
                    <input type="number" value={form.cycleOffDays} onChange={e => set('cycleOffDays', e.target.value)} min="0"
                      className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-cycle-off" />
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="text-zinc-500 text-[10px] uppercase tracking-wider block mb-1.5 font-heading">Notes</label>
                <input type="text" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="e.g. Targeting shoulder injury"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-700 text-white text-sm focus:border-[#E63946] focus:outline-none" data-testid="input-notes" />
              </div>

              <button onClick={handleSubmit} disabled={!form.peptideName || !form.dose || !form.startDate}
                className="w-full py-2.5 bg-[#E63946] text-white font-heading text-sm uppercase tracking-wider hover:opacity-90 transition-opacity disabled:opacity-30 flex items-center justify-center gap-2"
                data-testid="submit-cycle-btn">
                {editId ? 'Update Cycle' : 'Add Cycle'} <Check className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Cycle Cards */}
          {cycles.length > 0 && (
            <div className="space-y-3 mb-6" data-testid="cycle-list">
              {cycles.map(c => {
                const status = getCycleStatus(c);
                const endDate = c.endDate || addDays(c.startDate, c.cycleOnDays);
                const daysLeft = status === 'active' ? daysBetween(today(), endDate) : 0;
                const elapsed = status === 'active' ? daysBetween(c.startDate, today()) : c.cycleOnDays;
                const progress = Math.min((elapsed / c.cycleOnDays) * 100, 100);
                return (
                  <div key={c.id} className="bg-zinc-900/80 border border-zinc-800 p-4 group" data-testid={`cycle-${c.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color || '#E63946' }} />
                        <h4 className="text-white font-heading text-base">{c.peptideName}</h4>
                        <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-heading border" style={{ color: STATUS_COLORS[status], borderColor: `${STATUS_COLORS[status]}40` }}>{status}</span>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => togglePause(c.id)} className="p-1 text-zinc-500 hover:text-[#f59e0b] transition-colors" data-testid={`pause-${c.id}`}>
                          {c.paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => editCycle(c)} className="p-1 text-zinc-500 hover:text-[#00F0FF] transition-colors" data-testid={`edit-${c.id}`}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => deleteCycle(c.id)} className="p-1 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-[10px] text-zinc-400 mb-2">
                      <div><span className="text-zinc-600 block">Dose</span>{c.dose}</div>
                      <div><span className="text-zinc-600 block">Frequency</span>{c.frequency}</div>
                      <div><span className="text-zinc-600 block">Route</span>{c.route}</div>
                      <div><span className="text-zinc-600 block">Cycle</span>{c.cycleOnDays}d on / {c.cycleOffDays}d off</div>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-zinc-500 mb-2">
                      <span>{c.startDate}</span> <span className="text-zinc-700">→</span> <span>{endDate}</span>
                      {status === 'active' && daysLeft > 0 && <span className="text-green-500 ml-auto">{daysLeft} days left</span>}
                    </div>
                    {/* Progress bar */}
                    <div className="h-1.5 bg-zinc-800 overflow-hidden">
                      <div className="h-full transition-all duration-500" style={{ width: `${progress}%`, backgroundColor: c.color || '#E63946' }} />
                    </div>
                    {c.notes && <p className="text-zinc-600 text-xs italic mt-2">{c.notes}</p>}
                  </div>
                );
              })}
            </div>
          )}

          {cycles.length === 0 && !showForm && (
            <div className="text-center py-12 text-zinc-600 text-sm" data-testid="empty-state">
              <Syringe className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
              <p>No cycles tracked yet. Add your first peptide cycle above.</p>
            </div>
          )}

          {cycles.length > 0 && <SaveResultButton onSave={handleSave} saved={saved} />}

          {/* Safety Note */}
          <div className="mt-6 flex items-start gap-3 bg-[#f59e0b]/5 border border-[#f59e0b]/20 p-4">
            <AlertTriangle className="w-5 h-5 text-[#f59e0b] flex-shrink-0 mt-0.5" />
            <p className="text-zinc-300 text-xs"><strong className="text-[#f59e0b]">Disclaimer:</strong> This tool is for personal tracking only. Always consult with a healthcare provider before starting any peptide protocol. Peptides are research compounds and individual results vary.</p>
          </div>

          {/* Related Links */}
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/tools/peptide-dosing" className="text-[#E63946] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="link-peptide-dosing">
              Peptide Dosing Calculator <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/tools/reconstitution-guide" className="text-[#E63946] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors">
              Reconstitution Guide <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/blog?category=Peptides" className="text-[#E63946] text-xs font-heading uppercase tracking-wider inline-flex items-center gap-1 hover:text-white transition-colors">
              Peptide Guides <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
